import type { LayoutValues } from "../layoutEngine/layout.types";

type Point = {
  x: number;
  y: number;
};

// TODO - Its being used to calculate line traversal paths, but it needs to be removed from path calculation as the intended use-case is different.
const TILE_SIZE = 100;

const sectorToKeyMap = new Map<string, Set<string>>();
const dirtySectors = new Set<string>(); // for partial culling

/**
 * Convert the point into a sector ID which is in the form of
 * x,y where x and y are the sector coordinates, where it originates from.
 * @param point
 * @returns
 */
const pointToSector = (point: Point) => {
  const sectorX = Math.floor(point.x / TILE_SIZE);
  const sectorY = Math.floor(point.y / TILE_SIZE);
  return `${sectorX},${sectorY}`;
};

/**
 * Convert the dimensions of the component into a set of sectors that it occupies.
 * @param dimensions
 * @returns
 */
const layoutToSectors = ({ x, y, width, height }: LayoutValues) => {
  if (width === 0 || height === 0) {
    return new Set<string>();
  }
  const sectors = new Set<string>();
  const tileStartX = Math.floor(x / TILE_SIZE);
  const tileEndX = Math.ceil((x + width) / TILE_SIZE);
  const tileStartY = Math.floor(y / TILE_SIZE);
  const tileEndY = Math.ceil((y + height) / TILE_SIZE);

  for (let indexX = tileStartX; indexX < tileEndX; indexX++) {
    for (let indexY = tileStartY; indexY < tileEndY; indexY++) {
      sectors.add(`${indexX},${indexY}`);
    }
  }
  return sectors;
};

/**
 * Retrieve the components that are within the sector that the given
 * point resides in.
 * @param point
 * @returns {string[]} keys
 */
const retrieve = (point: Point): string[] => {
  // Find which sector does the point lie in.
  const sectorId = pointToSector(point);
  return retrieveFromSector(sectorId);
};

const retrieveFromSector = (sector: string) => {
  return Array.from(sectorToKeyMap.get(sector) || []);
};

const retrieveFromSectors = (sectors: Set<string>) => {
  const keys: string[] = [];
  sectors.forEach(sector => {
    const components = sectorToKeyMap.get(sector);
    if (components?.size) {
      keys.push(...Array.from(components));
    }
  });
  return new Set(keys);
};

/**
 * Insert the component into the given sectors
 * @param component
 * @param sectors
 */
const insertIn = ({ key, sectors }: { key: string; sectors: Set<string> }) => {
  sectors.forEach(sector => {
    let components = sectorToKeyMap.get(sector);
    dirtySectors.add(sector);
    if (!components) {
      components = new Set();
      sectorToKeyMap.set(sector, components);
    }
    components.add(key);
  });
};

const remove = ({
  key,
  layoutValues
}: {
  key: string;
  layoutValues: LayoutValues;
}) => {
  const sectors = layoutToSectors(layoutValues);
  if (!sectors) {
    return;
  }
  removeFrom({ key, sectors });
};

/**
 * Remove the component from the given sectors
 */
const removeFrom = ({
  key,
  sectors
}: {
  key: string;
  sectors: Set<string>;
}) => {
  sectors.forEach(sector => {
    const components = sectorToKeyMap.get(sector);
    if (components) {
      components.delete(key);
      if (components.size === 0) {
        sectorToKeyMap.delete(sector);
      }
    }
    // cleanup
  });
};

/**
 * Update the component in the component registry. We do this by:
 * 1. Removing the component from the sectors that it no longer occupies
 * 2. Adding the component to the sectors that it now occupies
 * @param component
 * @param lastDimensions
 */
const update = ({
  key,
  lastLayoutValues,
  nextLayoutValues
}: {
  key: string;
  lastLayoutValues: LayoutValues;
  nextLayoutValues: LayoutValues;
}) => {
  const previousSectors = layoutToSectors(lastLayoutValues);
  const newSectors = layoutToSectors(nextLayoutValues);

  const sectorsToRemoveFrom = new Set(previousSectors);
  const sectorsToAddTo = new Set(newSectors);

  // Find the sectors that are common between the two sets
  previousSectors?.forEach(sector => {
    if (newSectors.has(sector)) {
      sectorsToRemoveFrom.delete(sector);
      sectorsToAddTo.delete(sector);
    }
  });

  // Remove the component from the sectors that are not common
  removeFrom({ key, sectors: sectorsToRemoveFrom });

  // Add the component to the sectors that are not common
  insertIn({ key, sectors: sectorsToAddTo });
};

const getAllSectors = () => {
  return Array.from(sectorToKeyMap.keys());
};

const getDirtySectors = ({
  clearDirtySectors = true
}: {
  clearDirtySectors?: boolean;
}) => {
  const dirtySectorsArray = Array.from(dirtySectors);
  if (clearDirtySectors) {
    dirtySectors.clear();
  }
  return dirtySectorsArray;
};

export const spatialRegistry = {
  retrieve,
  retrieveFromSectors, // For culling,
  getAllSectors,
  getDirtySectors,
  layoutToSectors,
  update,
  remove,
  reset: () => {
    sectorToKeyMap.clear();
  }
};
